import axios, {AxiosError, AxiosInstance, AxiosResponse} from 'axios';
import {RequestMethods} from "@libs/axios/models/request-methods.enum";
import {ApiServiceConfig} from "@libs/axios/models/api-service-config.interface";
import {CustomAxiosRequestConfig} from "@libs/axios/models/custom-axios-request-config.interface";
import {CustomError} from "@libs/axios/models/custom-error.interface";

export class ApiService {
    private axiosInstance: AxiosInstance;
    private readonly BASE_URL: string;

    constructor(
        private config: ApiServiceConfig
    ) {
        this.BASE_URL = config.baseUrl;
        this.axiosInstance = axios.create({
            baseURL: this.BASE_URL,
        });

        this.axiosInstance.interceptors.response.use(
            this.handleSuccess,
            this.handleError.bind(this)
        );
    }

    private async http<T>(config: CustomAxiosRequestConfig): Promise<T> {
        try {
            if (!config.isPublic && this.config.authService) {
                const token = this.config.authService.getToken();
                if (token) {
                    if (!config.headers) {
                        config.headers = {};
                    }
                    config.headers.Authorization = "Bearer " + token;
                }
            }

            const response = await this.axiosInstance
                .request<T>(config)
                .then(res => {
                    if (config.successMessage && this.config.notificationService) {
                        this.config.notificationService.success(config.successMessage);
                    }
                    return res;
                });
            return response.data;
        } catch (error) {
            throw error as AxiosError;
        }
    }

    public async get<T>(path: string, opts?: Omit<CustomAxiosRequestConfig, 'method'>): Promise<T> {
        return this.http<T>({
            method: RequestMethods.GET,
            url: path,
            ...opts
        });
    }

    public async post<T>(path: string, body?: any, opts?: Omit<CustomAxiosRequestConfig, 'method'>): Promise<T> {
        return this.http<T>({
            method: RequestMethods.POST,
            url: path,
            data: body,
            ...opts
        });
    }

    public async put<T>(path: string, body?: any, opts?: Omit<CustomAxiosRequestConfig, 'method'>): Promise<T> {
        return this.http<T>({
            method: RequestMethods.PUT,
            url: path,
            data: body,
            ...opts
        });
    }

    public async patch<T>(path: string, body?: any, opts?: Omit<CustomAxiosRequestConfig, 'method'>): Promise<T> {
        return this.http<T>({
            method: RequestMethods.PATCH,
            url: path,
            data: body,
            ...opts
        });
    }

    public async delete<T>(path: string, opts?: Omit<CustomAxiosRequestConfig, 'method'>): Promise<T> {
        return this.http<T>({
            method: RequestMethods.DELETE,
            url: path,
            ...opts
        });
    }

    private handleSuccess(response: AxiosResponse) {
        return response;
    }

    private handleError(error: CustomError) {
        const {status} = error.response || {};
        const config = error.config as CustomAxiosRequestConfig;

        if (this.config.errorHandlingService && status && this.config.errorHandlingService.shouldUseCustomHandler(status, error)) {
            console.warn(`Custom handling for ${status}, skipping default redirect.`);
            this.config.errorHandlingService.handle(error);
        } else {
            if (config.skipDefaultErrorHandling) {
                return Promise.reject(error);
            }
            if (this.config.errorHandlingService) {
                this.config.errorHandlingService.handleDefault(error);
            }
        }

        return Promise.reject(error);
    }
}
